// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFormRepeater.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  isEmpty,
  keys,
  concat,
  toNumber,
  size,
  head,
  sortBy,
  has,
  identity,
  groupBy,
  findIndex,
  forEach,
  map,
  flow,
  get,
  trim,
  find,
  last,
  getOr,
  compact
} from 'lodash/fp';
import AppGlobal from 'Izenda.AppGlobal';
import RepeaterNode from './Izenda.RepeaterNode';
import ReportPartFormSmartTag from './Izenda.ReportPartFormSmartTag';
import ReportPartFormField from 'logic/models/Izenda.ReportPartFormField';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';

const getValueFromMappingField = ProcessDataUtils.getValueFromMappingField;
const DEFAULT_DATA = '[##DEFAULT_DATA##]';
const SUBTOTAL_START = TinyMCEConstants.SUBTOTAL_START;
const SUBTOTAL_END = TinyMCEConstants.SUBTOTAL_END;

export default class ReportPartFormRepeater {
  /**
   * Find all
   * @param  {String} string
   * @param  {String} sub string
   * @param  {Number} start
   * @param  {Object} source
   * @return {Object}
   */
  findAll(string: string, subString: string, start: number, source: any): any {
    if (!string || !string.length) {
      return source;
    }

    source = source || {};
    start = start || 0;

    const index = string.indexOf(subString, start);

    if (index < 0) {
      return source;
    }

    source[index] = subString;

    return this.findAll(string, subString, index + subString.length, source);
  }

  /**
   * create place holder for each pattern
   * it should have complicated structure to separate from user template
   * @param  {String} key
   * @return {String} [#&@YOUR_KEY@&#]
   */
  createPlaceHolder(key: string): string {
    return String.format('[#&@{0}@&#]', key);
  }

  /**
   * Get repated pattern, first level only
   * @param  {String} pattern
   * @return {Object}
   */
  getRepeaterPattern(pattern: string): string {
    const beginTags = {};
    const endTags = {};
    let tagOrder = [];
    const structure = [];
    const temp = [];
    let val, popVal;

    // 1. find of begin/end tag of repeater
    this.findAll(pattern, TinyMCEConstants.REPEATER_START, 0, beginTags);
    this.findAll(pattern, TinyMCEConstants.REPEATER_START_FORTABLE, 0, beginTags);

    this.findAll(pattern, TinyMCEConstants.REPEATER_END, 0, endTags);
    this.findAll(pattern, TinyMCEConstants.REPEATER_END_FORTABLE, 0, endTags);

    tagOrder = concat(keys(beginTags), keys(endTags));
    tagOrder = flow(
      map(toNumber),
      sortBy(identity)
    )(tagOrder);

    // 2. find position of repeater (first level only)
    temp.push(head(tagOrder));
    for (let i = 1; i < tagOrder.length; i++) {
      val = tagOrder[i];
      if (isBeginTag(val)) {
        temp.push(val);
      } else {
        popVal = temp.pop();
        temp.length === 0 && structure.push([popVal, val]);
      }
    }

    // 3. get pattern of each repeated-section
    const childPattern = map(pair => {
      const start = pair[0];
      const end = pair[1];
      return {
        placeHolderKey: this.createPlaceHolder([start, end].join('')),
        pattern: pattern.substring(start + size(beginTags[start]), end),
        start,
        end
      };
    }, structure);

    // 4. create placeholder for each pattern
    let newPattern = pattern;
    forEach(item => {
      newPattern = newPattern.replace(beginTags[item.start] + item.pattern + endTags[item.end], item.placeHolderKey);
    }, childPattern);

    return {
      placeHolderPattern: newPattern,
      repeaterParts: childPattern
    };

    function isBeginTag(indexId) {
      return has(indexId, beginTags);
    }
  }

  /**
   * Group data by field array
   * @param  {Array} fields]
   * @param  {Array} records
   * @param  {} groupNames
   * @return {Object}
   */
  groupData(fields: Array<any>, records: Array<any>) {
    const groupNamesArr = [];
    const groupNamesObj = {};
    let groupName;
    const getGroupName = record => {
      const vals = map(field => getValueFromMappingField(field.fieldInfo, record) || DEFAULT_DATA, fields);

      return vals.join('_');
    };

    const groupedData = groupBy(record => {
      groupName = getGroupName(record);

      if (!has(groupName, groupNamesObj)) {
        groupNamesObj[groupName] = true;
        groupNamesArr.push(groupName);
      }

      return groupName;
    }, records);

    return map(groupName => groupedData[groupName], groupNamesArr);
  }

  /**
   * Find and search all fields in pattern and replace by a placeholder
   * @param  {String} pattern
   * @param  {Array} mappingFields
   * @param  {Number} level of repeater contain fields
   * @return {Object}
   */
  getFields(pattern: string, mappingFields: Array<any>, repeaterLevel: number) {
    const listFields = [];

    const $patternHtml = $izenda('<div>' + pattern + '</div>');
    const fields = $patternHtml.find('field');
    fields.each((index, field) => {
      const fieldName = trim($izenda(field).text());
      const fieldInfo = find({ fieldNameAlias: fieldName }, mappingFields);
      const placeHolderKey = this.createPlaceHolder(fieldName);

      if (!fieldInfo) {
        return field;
      }
      fieldInfo.setRepeaterLevel && fieldInfo.setRepeaterLevel(repeaterLevel);
      listFields.push({
        placeHolderKey,
        fieldInfo
      });

      const fieldLength = head($izenda(field)).outerHTML.length;
      const beginTagIndex = pattern.indexOf('<field');
      if (beginTagIndex >= 0) {
        const fieldContent = pattern.substr(beginTagIndex, fieldLength);
        pattern = pattern.replace(fieldContent, placeHolderKey);
      }
    });

    return {
      fields: listFields,
      pattern
    };
  }

  /**
   * Get sub-totals data
   * @param {String} pattern
   * @param {Array} fields
   * @returns {Object} sub-total data
   */
  getSubTotals(pattern: string, fields: Array<any>): any {
    const $patternHtml = $izenda('<div>' + pattern + '</div>');
    const $subTotals = $patternHtml.find('subtotal');
    const subTotals = [];

    $subTotals.each((index, subTotal) => {
      const subTotalAlias = trim(subTotal.textContent);
      const groupFieldLevel = getOr('', 'fieldInfo.fieldNameAlias', last(fields));
      const placeHolderKey = this.createPlaceHolder(SUBTOTAL_START + subTotalAlias + SUBTOTAL_END);

      const subTotalLength = subTotal.outerHTML.length;
      const beginTagIndex = pattern.indexOf('<subtotal');
      if (beginTagIndex >= 0) {
        const subTotalContent = pattern.substr(beginTagIndex, subTotalLength);
        pattern = pattern.replace(subTotalContent, placeHolderKey);
      }

      subTotals.push({
        placeHolderKey,
        groupFieldLevel,
        subTotalAlias
      });
    });

    return {
      subTotals,
      pattern
    };
  }

  /**
   * Get outer data of repeater
   * @param {String} pattern
   * @param {Object} mappingFields
   * @param {Number} repeaterLevel
   * @returns {Object} outer data of repeater
   */
  getOuterDataOfRepeater(pattern: string, mappingFields: Array<any>, repeaterFields: Array<any>): Array<any> {
    const fieldData = this.getFields(pattern, mappingFields);
    repeaterFields = repeaterFields.concat(fieldData.fields);

    const subTotalData = this.getSubTotals(fieldData.pattern, repeaterFields);

    return {
      fields: fieldData.fields,
      subTotals: subTotalData.subTotals,
      pattern: subTotalData.pattern,
      repeaterFields: repeaterFields
    };
  }

  /**
   * Build smart tag configs by subtotal doms.
   * @param {Array} subTotalDoms
   * @param {number} level
   * @return{Array}
   */
  buildSmartTagInContentRepeater(subTotalDoms: Array<any>, level: number): Array<ReportPartFormSmartTag> {
    return flow(
      map(subTotalDom => {
        return new ReportPartFormSmartTag({
          name: trim(subTotalDom.textContent),
          type: 'subTotal',
          repeaterLevel: level,
          configField: {},
          subTotalDom: subTotalDom
        });
      }),
      compact
    )(subTotalDoms);
  }

  /**
   * Process subtotal smart tag.
   * @param {Array} smartTagInContent
   * @param {Array} fieldsMapping
   * @return{Array}
   */
  processSubTotal(smartTagInContent: Array<ReportPartFormSmartTag>, fieldsMapping: Array<any>): Array<any> {
    const fieldSupportSubtotal = [];
    forEach(subTotal => {
      const field = find(['name', subTotal.name], fieldsMapping);
      if (field) {
        fieldSupportSubtotal.push({ fieldInfo: field });
        subTotal.configField = { fieldInfo: field };
      }
    }, smartTagInContent);
    return fieldSupportSubtotal;
  }

  /**
   * Parse user template in to HTML string
   * @param  {String} pattern
   * @param  {Object} previewData
   * @return {String}
   */
  buildRepeaterTree(pattern: string, fieldsMapping: Array<any>, level: number = 0, repeaterNodes: Array<RepeaterNode> = []) {
    try {
      const repeatedPattern = this.getRepeaterPattern(pattern);
      const fields = this.getFields(get('placeHolderPattern', repeatedPattern), fieldsMapping, level);

      const hasRepeatPart = !flow(
        get('repeaterParts'),
        isEmpty
      )(repeatedPattern);

      // get smart tag on this
      let subTotalDoms;
      try {
        subTotalDoms = $izenda(`<div>${repeatedPattern.placeHolderPattern}</div>`).find('subtotal');
      } catch (e) {
        // do nothing
      }

      // get field smart tag
      const smartTagInContent = this.buildSmartTagInContentRepeater(subTotalDoms, level);
      const fieldSupportSubtotal = this.processSubTotal(smartTagInContent, fieldsMapping);

      const repeaterNode = new RepeaterNode({
        childNodes: [],
        level,
        fields: get('fields', fields),
        smartTagInContent,
        fieldSupportSubtotal,
        placeHolderPattern: get('placeHolderPattern', repeatedPattern),
        placeHolderKey: map(item => item.placeHolderKey, repeatedPattern.repeaterParts),
        pattern: map(item => item.pattern, repeatedPattern.repeaterParts)
      });

      repeaterNodes.push(repeaterNode);

      if (!hasRepeatPart) {
        return;
      }

      forEach(repeatPart => {
        this.buildRepeaterTree(repeatPart.pattern, fieldsMapping, level + 1, repeaterNode.childNodes);
      }, repeatedPattern.repeaterParts);
    } catch (e) {
      // do nothing
    }
  }

  /**
   * Build HTML to array of repeater and its fields
   * @param  {String} pattern
   * @param  {Object} previewData
   * @return {String}
   */
  generateData(
    pattern,
    previewData,
    visibleElement,
    reportPart,
    reportId,
    embeddedSubReports,
    overideFilterValue,
    subTotalMapping,
    repeaterFields = []
  ) {
    try {
      const repeatedPattern = this.getRepeaterPattern(pattern);
      const outerData = this.getOuterDataOfRepeater(
        get('placeHolderPattern', repeatedPattern),
        previewData.fieldsMapping,
        repeaterFields
      );

      const hasRepeatPart =
        flow(
          get('repeaterParts'),
          size
        )(repeatedPattern) > 0;

      const groupedData = hasRepeatPart ? this.groupData(outerData.fields, previewData.records) : previewData.records;

      // generic data
      const htmlContent = map(groupRecords => {
        let outerContent = this.parseData(
          outerData,
          hasRepeatPart ? head(groupRecords) : groupRecords,
          visibleElement,
          reportPart,
          reportId,
          previewData.fieldsMapping,
          embeddedSubReports,
          overideFilterValue,
          subTotalMapping
        );
        let innerContent = '';

        if (hasRepeatPart) {
          forEach(repeatPart => {
            innerContent = this.generateData(
              repeatPart.pattern,
              { records: groupRecords, fieldsMapping: previewData.fieldsMapping },
              visibleElement,
              reportPart,
              reportId,
              embeddedSubReports,
              overideFilterValue,
              subTotalMapping,
              outerData.repeaterFields
            );

            outerContent = outerContent.replace(repeatPart.placeHolderKey, innerContent);
            innerContent = '';
          }, repeatedPattern.repeaterParts);
        }

        return outerContent.indexOf('<tr') >= 0 ? outerContent : outerContent + '<br />';
      }, groupedData);

      return htmlContent.join('');
    } catch (e) {
      return '<h1>FAIL</h1>';
    }
  }

  /**
   * Replace field by its real data and apply style also
   * @param  {String} pattern
   * @param  {Array} fields
   * @param  {Object} record
   * @return {String}
   */
  parseData(
    { pattern, fields, subTotals },
    record,
    visibleElement,
    reportPart,
    reportId,
    fieldsMapping,
    embeddedSubReports,
    overideFilterValue,
    subTotalMapping
  ) {
    const reportPartFormField = new ReportPartFormField();

    // Iterates over fields and replace its data by placeholder
    fields.forEach(field => {
      const columnName = get('columnName', field.fieldInfo);
      const mapValue = ReportPartUtils.getFieldValue(columnName, record);
      mapValue.value = String.commonStringValue(mapValue.value, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);

      const fieldInfo = find({ fieldName: field.fieldInfo.fieldNameAlias }, visibleElement) || {};
      if (!isEmpty(fieldInfo)) {
        // this index must match the index position of record in array list
        const recordIndex = findIndex(testRecord => testRecord === record, reportPart.previewDataRecords);

        const data = reportPartFormField.formatField(
          {
            reportPartElm: fieldInfo,
            columnName
          },
          mapValue,
          reportId,
          recordIndex,
          reportPart,
          record,
          fieldsMapping,
          overideFilterValue
        );
        if (data.embeddedSubReports.length > 0) {
          embeddedSubReports.data = embeddedSubReports.data.concat(data.embeddedSubReports);
        }

        pattern = pattern.replace(field.placeHolderKey, data.newText);
      } else {
        pattern = pattern.replace(field.placeHolderKey, '');
      }
    });

    // Iterates over subtotals and replace its data by placeholder
    subTotals.forEach(({ placeHolderKey, subTotalAlias, groupFieldLevel }) => {
      const columnName = flow(
        find({
          fieldNameAlias: subTotalAlias,
          ...(!isEmpty(groupFieldLevel) && { groupFieldLevel: groupFieldLevel })
        }),
        get('columnName')
      )(subTotalMapping);

      const fieldElement = find({ fieldName: subTotalAlias }, visibleElement);
      const dataType = get('properties.dataFormattings.subTotal.dataType', fieldElement);
      const subTotalFormat = get('properties.dataFormattings.subTotal.format', fieldElement);
      const subTotalValue =
        subTotalFormat && ReportPartUtils.applyFormater(dataType, subTotalFormat.format, record[columnName]);

      pattern = pattern.replace(placeHolderKey, subTotalValue);
    });

    return pattern;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartFormRepeater.js